import React, { useState, useRef, useEffect } from 'react';
import { X, Plus, Type, RefreshCw, Loader } from 'lucide-react';

function SentimentDialog({ pendingSentiments, setPendingSentiments, onConfirm, onCancel, onRescan }) {
  const [items, setItems] = useState(pendingSentiments || []);
  const [inputValue, setInputValue] = useState('');
  const [isRescanning, setIsRescanning] = useState(false);
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => listEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  const addItem = () => {
    const val = inputValue.trim();
    if (!val) return;
    if (!items.includes(val)) {
      setItems(prev => [...prev, val]);
      setTimeout(scrollToBottom, 50);
    }
    setInputValue('');
    inputRef.current?.focus();
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  const handleRescan = async () => {
    if (!onRescan) return;
    setIsRescanning(true);
    try {
      const newSentiments = await onRescan();
      if (newSentiments?.length) {
        setItems(prev => {
          const existing = new Set(prev.map(s => s.toLowerCase()));
          const toAdd = newSentiments.filter(s => !existing.has(s.toLowerCase()));
          if (toAdd.length === 0) return prev;
          return [...prev, ...toAdd];
        });
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Rescan error:', err);
    } finally {
      setIsRescanning(false);
    }
  };

  const handleSave = () => {
    setPendingSentiments(items);
    setTimeout(onConfirm, 0);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm" onClick={onCancel}>
      <div
        className="bg-white rounded-xl border border-beige shadow-xl max-w-md w-full max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="sentiment-dialog"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-beige shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Type size={18} className="text-sage shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-medium text-charcoal">Detected Text</h3>
              <p className="text-xs text-gray-soft">Review and edit the detected sentiments before adding</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {onRescan && (
              <button
                type="button"
                onClick={handleRescan}
                disabled={isRescanning}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-sage text-sage rounded-lg hover:bg-sage hover:text-white transition-colors disabled:opacity-50"
                data-testid="rescan-sentiments-btn"
              >
                {isRescanning ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                Rescan
              </button>
            )}
            <button onClick={onCancel} className="p-1 text-gray-soft hover:text-charcoal rounded transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 min-h-[120px] max-h-[300px]">
          {isRescanning ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader size={24} className="animate-spin text-sage mb-2" />
              <p className="text-sm text-gray-soft">Re-scanning image for text...</p>
            </div>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-soft text-center py-6">No sentiments detected. Type below to add one.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {items.map((item, idx) => (
                <span key={`${item}-${idx}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sage-light text-charcoal rounded-full text-sm group">
                  {item}
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="shrink-0 p-0.5 text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`remove-detected-sentiment-${idx}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
              <div ref={listEndRef} />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-beige shrink-0">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a sentiment..."
              className="flex-1 px-3 py-2.5 border border-beige rounded-lg text-sm text-charcoal placeholder:text-gray-300 focus:outline-none focus:border-sage"
              data-testid="detected-sentiment-input"
            />
            <button
              type="button"
              onClick={addItem}
              disabled={!inputValue.trim()}
              className="px-4 py-2.5 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              data-testid="add-detected-sentiment-btn"
            >
              <Plus size={16} />
              Add
            </button>
          </div>
          <p className="text-xs text-gray-soft mt-1.5">
            Press <kbd className="px-1 py-0.5 bg-cream rounded text-[10px] font-medium">Enter</kbd> or click <strong>+ Add</strong> to add a sentiment
          </p>
        </div>

        <div className="px-5 py-3 border-t border-beige flex justify-end gap-3 shrink-0">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-soft hover:text-charcoal border border-beige rounded-lg transition-colors" data-testid="cancel-sentiment-dialog-btn">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="px-4 py-2 bg-sage text-white rounded-lg text-sm font-medium hover:bg-sage-dark transition-colors" data-testid="confirm-sentiment-dialog-btn">
            Done ({items.length})
          </button>
        </div>
      </div>
    </div>
  );
}

export default SentimentDialog;
